<style>
	.select-grouped-container {
		position: relative;
		display: inline-block;
		width: 100%;
	}

	.select-grouped-button {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		padding: 0.5rem 0.75rem;
		background: white;
		border: 1px solid #d1d5db;
		border-radius: 0.375rem;
		font-size: 0.875rem;
		line-height: 1.5;
		cursor: pointer;
		transition: all 0.15s ease;
	}

	.select-grouped-button:hover {
		border-color: #9ca3af;
	}

	.select-grouped-button:focus {
		outline: none;
		border-color: #3b82f6;
		box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
	}

	.select-grouped-button.open {
		border-color: #3b82f6;
		box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
	}

	.select-grouped-button.disabled {
		opacity: 0.5;
		cursor: not-allowed;
		background: #f9fafb;
	}

	.select-grouped-value {
		flex: 1;
		text-align: left;
		color: #374151;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.select-grouped-button.disabled .select-grouped-value {
		color: #9ca3af;
	}

	.select-grouped-chevron {
		transition: transform 0.15s ease;
		color: #6b7280;
	}

	.select-grouped-chevron.rotate-180 {
		transform: rotate(180deg);
	}

	.select-grouped-dropdown,
	.select-grouped-group-dropdown {
		position: absolute;
		z-index: 1000;
		min-width: 12rem;
		max-width: 20rem;
		max-height: 20rem;
		overflow-y: auto;
		background: white;
		border: 1px solid #d1d5db;
		border-radius: 0.375rem;
		box-shadow:
			0 4px 6px -1px rgba(0, 0, 0, 0.1),
			0 2px 4px -1px rgba(0, 0, 0, 0.06);
		padding: 0.25rem 0;
	}

	.select-grouped-group {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 0.5rem 0.75rem;
		cursor: pointer;
		transition: background-color 0.15s ease;
	}

	.select-grouped-group:hover,
	.select-grouped-group.focused {
		background: #f3f4f6;
	}

	.select-grouped-group.open {
		background: #eff6ff;
		color: #1d4ed8;
	}

	.select-grouped-group-content {
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}

	.select-grouped-group-label {
		font-weight: 500;
		color: #374151;
	}

	.select-grouped-group.open .select-grouped-group-label {
		color: #1d4ed8;
	}

	.select-grouped-group-count {
		font-size: 0.75rem;
		color: #6b7280;
		background: #f3f4f6;
		padding: 0.125rem 0.375rem;
		border-radius: 0.25rem;
	}

	.select-grouped-group.open .select-grouped-group-count {
		background: #dbeafe;
		color: #1e40af;
	}

	.select-grouped-group-chevron {
		color: #9ca3af;
		transition: color 0.15s ease;
	}

	.select-grouped-group.open .select-grouped-group-chevron {
		color: #1d4ed8;
	}

	.select-grouped-item {
		display: flex;
		flex-direction: column;
		padding: 0.5rem 0.75rem;
		cursor: pointer;
		transition: background-color 0.15s ease;
	}

	.select-grouped-item:hover,
	.select-grouped-item.focused {
		background: #f3f4f6;
	}

	.select-grouped-item.selected {
		background: #eff6ff;
		color: #1d4ed8;
	}

	.select-grouped-item.disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.select-grouped-item-label {
		font-weight: 500;
		color: #374151;
	}

	.select-grouped-item.selected .select-grouped-item-label {
		color: #1d4ed8;
	}

	.select-grouped-item-description {
		font-size: 0.75rem;
		color: #6b7280;
		margin-top: 0.125rem;
	}

	.select-grouped-item.selected .select-grouped-item-description {
		color: #1e40af;
	}

	/* Scrollbar styling */
	.select-grouped-dropdown::-webkit-scrollbar,
	.select-grouped-group-dropdown::-webkit-scrollbar {
		width: 6px;
	}

	.select-grouped-dropdown::-webkit-scrollbar-track,
	.select-grouped-group-dropdown::-webkit-scrollbar-track {
		background: #f1f5f9;
		border-radius: 3px;
	}

	.select-grouped-dropdown::-webkit-scrollbar-thumb,
	.select-grouped-group-dropdown::-webkit-scrollbar-thumb {
		background: #cbd5e1;
		border-radius: 3px;
	}

	.select-grouped-dropdown::-webkit-scrollbar-thumb:hover,
	.select-grouped-group-dropdown::-webkit-scrollbar-thumb:hover {
		background: #94a3b8;
	}
</style>

<script lang="ts">
	import {
		computePosition,
		flip,
		offset as offsetModifier,
		autoUpdate,
		shift
	} from "@floating-ui/dom";
	import { createEventDispatcher, onDestroy, onMount, tick } from "svelte";
	import { portal } from "design/helpers/svelte-portal";
	import { mousedownOutside } from "$lib/actions";
	import { BROWSER } from "esm-env";
	import { subtlepop } from "$lib/transitions/pop";
	import * as Icons from "$lib/Components/Icons";

	const dispatch = createEventDispatcher();

	export let options: GroupedChoiceOption[] = [];
	export let placeholder: string = "(select)";
	export let value: string = "";
	export let disabled: boolean = false;
	export let required: boolean = false;
	export let id: string = "";
	export let name: string = "";
	export let tabindex: number = 0;

	interface GroupedChoiceOption {
		label: string;
		value: string;
		group?: string;
		description?: string;
		disabled?: boolean;
		embed?: any;
		meta?: any;
	}

	let open = false;
	let selectedItem: GroupedChoiceOption | null = null;
	let openGroup: string | null = null;
	let selectRef: HTMLButtonElement;
	let dropdownRef: HTMLDivElement;
	let groupDropdownRef: HTMLDivElement;
	let cleanup: (() => void) | null = null;
	let groupCleanup: (() => void) | null = null;
	let focusedIndex = -1;
	let focusedGroupIndex = -1;
	let groupButtonRef: HTMLElement | null = null;

	$: {
		if (value && options.length > 0) {
			selectedItem = options.find((opt) => opt.value === value) || null;
		}
	}

	$: groups = Array.from(
		new Set(options.map((opt) => opt.group).filter(Boolean))
	);
	$: groupedOptions = groups.reduce(
		(acc, group) => {
			acc[group!] = options.filter((opt) => opt.group === group);
			return acc;
		},
		{} as Record<string, GroupedChoiceOption[]>
	);

	$: ungroupedOptions = options.filter((opt) => !opt.group);
	
	// Create a flat array of all navigable items for consistent focus management
	$: navigableItems = [
		...groups.map((group) => ({ type: 'group', group })),
		...ungroupedOptions.filter(opt => !opt.disabled).map((option) => ({ type: 'option', option }))
	];

	const openDropdown = async () => {
		if (disabled || open) return;
		open = true;
		focusedIndex = -1;
		await tick();

		if (!selectRef || !dropdownRef) return;

		cleanup = autoUpdate(selectRef, dropdownRef, async () => {
			if (!selectRef || !dropdownRef) return;

			const { x, y } = await computePosition(selectRef, dropdownRef, {
				placement: "bottom-start",
				middleware: [
					flip({ fallbackPlacements: ["top-start", "bottom-end", "top-end"] }),
					shift({ padding: 8 }),
					offsetModifier(4)
				]
			});

			Object.assign(dropdownRef.style, {
				left: `${x}px`,
				top: `${y}px`
			});
		});
	};

	const closeDropdown = () => {
		open = false;
		openGroup = null;
		focusedIndex = -1;
		focusedGroupIndex = -1;
		if (cleanup) {
			cleanup();
			cleanup = null;
		}
		if (groupCleanup) {
			groupCleanup();
			groupCleanup = null;
		}
	};

	const openGroupDropdown = async (group: string, buttonEl: HTMLElement) => {
		if (openGroup === group) {
			closeGroupDropdown();
			return;
		}

		openGroup = group;
		groupButtonRef = buttonEl;
		focusedGroupIndex = -1;

		await tick();

		if (!groupButtonRef || !groupDropdownRef) return;

		groupCleanup = autoUpdate(groupButtonRef, groupDropdownRef, async () => {
			if (!groupButtonRef || !groupDropdownRef) return;

			const { x, y } = await computePosition(groupButtonRef, groupDropdownRef, {
				placement: "right-start",
				middleware: [
					flip({ fallbackPlacements: ["right-end", "left-start", "left-end"] }),
					shift({ padding: 8 }),
					offsetModifier(4)
				]
			});

			Object.assign(groupDropdownRef.style, {
				left: `${x}px`,
				top: `${y}px`
			});
		});
	};

	const closeGroupDropdown = () => {
		openGroup = null;
		focusedGroupIndex = -1;
		if (groupCleanup) {
			groupCleanup();
			groupCleanup = null;
		}
	};

	const selectOption = (option: GroupedChoiceOption) => {
		if (option.disabled) return;

		selectedItem = option;
		value = option.value;
		closeDropdown();

		dispatch("change", { value: option.value, option });
		selectRef?.focus();
	};

	const handleKeydown = (event: KeyboardEvent) => {
		if (!open) {
			if (event.key === "Enter" || event.key === " ") {
				event.preventDefault();
				openDropdown();
			}
			return;
		}

		switch (event.key) {
			case "Escape":
				event.preventDefault();
				closeDropdown();
				break;
			case "ArrowDown":
				event.preventDefault();
				if (openGroup) {
					navigateGroupItems(1);
				} else {
					navigateItems(1);
				}
				break;
			case "ArrowUp":
				event.preventDefault();
				if (openGroup) {
					navigateGroupItems(-1);
				} else {
					navigateItems(-1);
				}
				break;
			case "ArrowRight":
				event.preventDefault();
				if (!openGroup) {
					expandGroup();
				}
				break;
			case "ArrowLeft":
				event.preventDefault();
				if (openGroup) {
					closeGroupDropdown();
				}
				break;
			case "Enter":
				event.preventDefault();
				handleEnter();
				break;
		}
	};

	const getAllSelectableItems = () => {
		const items: {
			option: GroupedChoiceOption;
			isGroup: boolean;
			group?: string;
		}[] = [];

		groups.forEach((group) => {
			items.push({
				option: { label: group, value: group, group },
				isGroup: true,
				group
			});
		});

		ungroupedOptions.forEach((option) => {
			if (!option.disabled) {
				items.push({ option, isGroup: false });
			}
		});

		return items;
	};

	const navigateItems = (direction: number) => {
		if (openGroup) return;

		if (navigableItems.length === 0) return;

		if (focusedIndex === -1) {
			// No item focused, navigate to first or last item based on direction
			focusedIndex = direction > 0 ? 0 : navigableItems.length - 1;
		} else {
			focusedIndex = Math.max(
				0,
				Math.min(navigableItems.length - 1, focusedIndex + direction)
			);
		}
	};

	const navigateGroupItems = (direction: number) => {
		if (!openGroup) return;

		const groupOptions =
			groupedOptions[openGroup]?.filter((opt) => !opt.disabled) || [];
		if (groupOptions.length === 0) return;

		if (focusedGroupIndex === -1) {
			// No item focused, navigate to first or last item based on direction
			focusedGroupIndex = direction > 0 ? 0 : groupOptions.length - 1;
		} else {
			focusedGroupIndex = Math.max(
				0,
				Math.min(groupOptions.length - 1, focusedGroupIndex + direction)
			);
		}
	};

	const expandGroup = () => {
		if (focusedIndex === -1 || focusedIndex >= navigableItems.length) return;
		
		const currentItem = navigableItems[focusedIndex];
		
		if (currentItem?.type === 'group') {
			const groupButton = dropdownRef?.querySelector(
				`[data-group="${currentItem.group}"]`
			) as HTMLElement;
			if (groupButton) {
				openGroupDropdown(currentItem.group, groupButton);
			}
		}
	};

	const handleEnter = () => {
		if (openGroup) {
			const groupOptions =
				groupedOptions[openGroup]?.filter((opt) => !opt.disabled) || [];
			const selectedOption = groupOptions[focusedGroupIndex];
			if (selectedOption) {
				selectOption(selectedOption);
			}
		} else {
			if (focusedIndex === -1 || focusedIndex >= navigableItems.length) return;
			
			const currentItem = navigableItems[focusedIndex];
			if (currentItem?.type === 'group') {
				expandGroup();
			} else if (currentItem?.type === 'option') {
				selectOption(currentItem.option);
			}
		}
	};

	onMount(() => {
		if (BROWSER) {
			document.addEventListener("keydown", handleKeydown);
		}
	});

	onDestroy(() => {
		if (BROWSER) {
			document.removeEventListener("keydown", handleKeydown);
			closeDropdown();
		}
	});
</script>

<div class="select-grouped-container">
	<button
		type="button"
		bind:this="{selectRef}"
		class="select-grouped-button"
		class:disabled="{disabled}"
		class:open="{open}"
		aria-haspopup="listbox"
		aria-expanded="{open}"
		id="{id}"
		name="{name}"
		tabindex="{tabindex}"
		disabled="{disabled}"
		on:click="{openDropdown}"
		on:keydown="{handleKeydown}">
		<span class="select-grouped-value">
			{selectedItem?.label || placeholder}
		</span>
		<div class="select-grouped-chevron {open ? 'rotate-180' : ''}">
			<Icons.ChevronDown size="1rem" />
		</div>
	</button>
</div>

{#if open}
	<div
		bind:this="{dropdownRef}"
		use:portal="{'body'}"
		use:mousedownOutside="{{
			callbackFunction: closeDropdown,
			ignoreElements: [selectRef, groupDropdownRef].filter(Boolean)
		}}"
		class="select-grouped-dropdown"
		role="listbox"
		aria-multiselectable="false"
		transition:subtlepop="{{ duration: 150 }}">
		{#each groups as group, index}
			{@const groupOptions = groupedOptions[group]}
			{@const isGroupFocused = focusedIndex === index}
			{@const isGroupOpen = openGroup === group}

			<div
				class="select-grouped-group"
				class:focused="{isGroupFocused}"
				class:open="{isGroupOpen}"
				data-group="{group}"
				role="button"
				tabindex="0"
				aria-expanded="{isGroupOpen}"
				aria-haspopup="listbox"
				on:click="{(e) => {
					e.stopPropagation();
					openGroupDropdown(group, e.currentTarget);
				}}"
				on:keydown="{(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						openGroupDropdown(group, e.currentTarget);
					}
				}}"
				on:mouseenter="{() => {
					if (!openGroup) focusedIndex = index;
				}}">
				<div class="select-grouped-group-content">
					<span class="select-grouped-group-label">{group}</span>
					<span class="select-grouped-group-count"
						>({groupOptions.length})</span>
				</div>
				<div class="select-grouped-group-chevron">
					<Icons.ChevronRight size="0.875rem" />
				</div>
			</div>
		{/each}

		{#each ungroupedOptions.filter(opt => !opt.disabled) as option, index}
			{@const itemIndex = groups.length + index}
			{@const isItemFocused = focusedIndex === itemIndex}

			<div
				class="select-grouped-item"
				class:focused="{isItemFocused}"
				class:disabled="{option.disabled}"
				class:selected="{selectedItem?.value === option.value}"
				role="button"
				tabindex="0"
				aria-disabled="{option.disabled}"
				on:click="{() => selectOption(option)}"
				on:keydown="{(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						selectOption(option);
					}
				}}"
				on:mouseenter="{() => {
					if (!option.disabled) focusedIndex = itemIndex;
				}}">
				<span class="select-grouped-item-label">{option.label}</span>
				{#if option.description}
					<span class="select-grouped-item-description"
						>{option.description}</span>
				{/if}
			</div>
		{/each}
	</div>
{/if}

{#if openGroup}
	<div
		bind:this="{groupDropdownRef}"
		use:portal="{'body'}"
		class="select-grouped-group-dropdown"
		role="listbox"
		aria-multiselectable="false"
		transition:subtlepop="{{ duration: 150 }}">
		{#each groupedOptions[openGroup] || [] as option, index}
			{@const isItemFocused = focusedGroupIndex === index}

			<div
				class="select-grouped-item"
				class:focused="{isItemFocused}"
				class:disabled="{option.disabled}"
				class:selected="{selectedItem?.value === option.value}"
				role="button"
				tabindex="0"
				aria-disabled="{option.disabled}"
				on:click="{() => selectOption(option)}"
				on:keydown="{(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						selectOption(option);
					}
				}}"
				on:mouseenter="{() => {
					if (!option.disabled) focusedGroupIndex = index;
				}}">
				<span class="select-grouped-item-label">{option.label}</span>
				{#if option.description}
					<span class="select-grouped-item-description"
						>{option.description}</span>
				{/if}
			</div>
		{/each}
	</div>
{/if}
